/**
 * vue-router resolves a returned `selector` with document.querySelector (getElementById for `#<digit>`),
 * which throws on fragments from outside links like `#waiver?waiverOnly=1` or an unreplaced merge tag.
 *
 * @param {string} hash
 * @returns {boolean}
 */
function isUsableHashSelector(hash) {
  if (/^#\d/.test(hash)) {
    return true;
  }
  try {
    document.querySelector(hash);
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {import('vue-router').Route} to
 * @param {import('vue-router').Route} from
 * @param {{ x: number, y: number } | null} savedPosition
 */
export function scrollBehavior(to, from, savedPosition) {
  // Back/forward: restore the saved offset. Defining scrollBehavior switches the
  // router to manual scroll restoration, so we own this case. Setup screens hydrate
  // their content asynchronously, so the page often isn't tall enough to honor the
  // offset at navigation time (the scroll would clamp near the top). Wait per frame
  // until the layout can accommodate it, then restore — resolving the moment it's
  // ready, with a frame budget so it can never hang if the page is now shorter.
  if (savedPosition) {
    return new Promise((resolve) => {
      let frames = 0;
      const restore = () => {
        const ready = document.documentElement.scrollHeight >= savedPosition.y + window.innerHeight;
        if (ready || frames >= 50) {
          resolve(savedPosition);
          return;
        }
        frames += 1;
        requestAnimationFrame(restore);
      };
      requestAnimationFrame(restore);
    });
  }
  if (to.hash && isUsableHashSelector(to.hash)) return { selector: to.hash };
  // Same screen — e.g. opening an edit drawer/tab on a List/Detail :action?/:id?
  // route (which keeps one route name across list/create/edit). Leave the scroll
  // alone so the list behind the drawer doesn't jump to the top.
  if (to.name && from.name && to.name === from.name) return false;
  // Navigating to a different screen — start at the top.
  return { x: 0, y: 0 };
}
